import { useState, useEffect, useCallback } from 'react';
import {
  FileText, RefreshCw, Tags, CheckCircle2, Loader2,
  ChevronRight, ChevronDown, ChevronUp, Table2, Trash2,
  Users, Building2, MapPin, Lightbulb, Scale, BookOpen,
  FlaskConical, Target, Gauge, AlertTriangle, Shield, Plus,
  Star, Scissors, Search,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ingestionService from '../api';

// ── Taxonomy helpers ──────────────────────────────────────────────────────────

function TaxonomyTab() {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newNodeName, setNewNodeName] = useState('');
  const [collapsed, setCollapsed] = useState(new Set());

  const loadTaxonomy = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ingestionService.getTaxonomy();
      setNodes(Array.isArray(data) ? data : []);
    } catch { setNodes([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTaxonomy(); }, [loadTaxonomy]);

  const toggleCollapse = (id) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await ingestionService.generateTaxonomy();
      await loadTaxonomy();
      setCollapsed(new Set());
    } finally { setGenerating(false); }
  };

  const handleApproveAll = async () => {
    await ingestionService.approveAll();
    await loadTaxonomy();
  };

  const handleAddNode = async () => {
    if (!newNodeName.trim()) return;
    await ingestionService.createNode({ name: newNodeName.trim() });
    setNewNodeName('');
    await loadTaxonomy();
  };

  const handleDelete = async (node) => {
    if (!window.confirm(`Delete "${node.name}"?`)) return;
    await ingestionService.deleteNode(node.id);
    await loadTaxonomy();
  };

  const levelColors = [
    { bg: 'bg-blue-50', border: 'border-l-blue-400', text: 'text-primaryDark' },
    { bg: 'bg-sky-50', border: 'border-l-sky-400', text: 'text-sky-700' },
    { bg: 'bg-indigo-50', border: 'border-l-indigo-400', text: 'text-indigo-800' },
    { bg: 'bg-teal-50', border: 'border-l-teal-400', text: 'text-teal-800' },
  ];

  const renderNode = (node, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isCollapsed = collapsed.has(node.id);
    const colors = levelColors[Math.min(depth, levelColors.length - 1)];

    return (
      <div key={node.id}>
        <div
          className={`flex items-center justify-between py-2.5 px-3 rounded-md mb-1 border-l-4 ${colors.border} ${colors.bg} hover:brightness-95 transition-all`}
          style={{ marginLeft: `${depth * 20}px` }}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {hasChildren ? (
              <button onClick={() => toggleCollapse(node.id)} className="p-0.5 rounded hover:bg-white/50 flex-shrink-0">
                <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
              </button>
            ) : (
              <span className="w-5 flex-shrink-0" />
            )}
            <span className={`font-medium ${colors.text} text-sm`}>{node.name}</span>
            {hasChildren && <span className="text-xs text-gray-400 flex-shrink-0">({node.children.length})</span>}
            {node.description && <span className="text-xs text-gray-500 truncate ml-1">- {node.description}</span>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              (node.approved || node.is_approved) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {(node.approved || node.is_approved) ? 'Approved' : 'Pending'}
            </span>
            <button onClick={() => handleDelete(node)} className="p-1 rounded hover:bg-white/60">
              <Trash2 className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
            </button>
          </div>
        </div>
        {hasChildren && !isCollapsed && <div>{node.children.map((child) => renderNode(child, depth + 1))}</div>}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tags className="w-4 h-4" />}
          {generating ? 'Generating...' : 'AI Generate Taxonomy'}
        </button>
        <button onClick={handleApproveAll} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">
          <CheckCircle2 className="w-4 h-4" /> Approve All
        </button>
        <button onClick={loadTaxonomy} className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 rounded-t-lg">
          <h3 className="font-semibold text-gray-900">Taxonomy Tree</h3>
          {nodes.length > 0 && (
            <p className="text-sm text-gray-500 mt-0.5">{nodes.length} top-level {nodes.length === 1 ? 'category' : 'categories'}</p>
          )}
        </div>
        <div className="p-4">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : nodes.length === 0 ? (
            <p className="text-gray-500 text-center py-8 text-sm">No taxonomy generated yet. Click "AI Generate Taxonomy" to start.</p>
          ) : (
            <div className="space-y-0.5">{nodes.map((n) => renderNode(n))}</div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="New category name..."
            value={newNodeName}
            onChange={(e) => setNewNodeName(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleAddNode}
            disabled={!newNodeName.trim()}
            className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Add Node
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Q&A section ───────────────────────────────────────────────────────────────

function QASection() {
  const [userQuestion, setUserQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [qaAnswer, setQaAnswer] = useState(null);
  const [qaHistory, setQaHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try { setQaHistory(await ingestionService.getQuestionHistory(20)); } catch { setQaHistory([]); }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleAskQuestion = async () => {
    if (!userQuestion.trim()) return;
    setAsking(true);
    setQaAnswer(null);
    try {
      const result = await ingestionService.askQuestion(userQuestion.trim());
      setQaAnswer(result);
      setUserQuestion('');
      loadHistory();
    } catch (err) {
      alert(err?.message ?? 'Failed to get answer');
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 rounded-t-lg">
          <h3 className="font-semibold text-gray-900">Ask a Question</h3>
          <p className="text-sm text-gray-500 mt-0.5">Ask anything about your documents. Answers are grounded in your indexed content.</p>
        </div>
        <div className="p-6">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="e.g. What risk mitigation strategies are documented?"
              value={userQuestion}
              onChange={(e) => setUserQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !asking && handleAskQuestion()}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleAskQuestion}
              disabled={!userQuestion.trim() || asking}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ask'}
            </button>
          </div>

          {qaAnswer && (
            <div className="mt-6 border rounded-lg bg-white">
              <div className="px-4 py-3 border-b bg-gray-50 rounded-t-lg">
                <p className="text-sm font-semibold text-gray-700">Q: {qaAnswer.question}</p>
              </div>
              <div className="px-4 py-4 prose prose-sm max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ node, ...props }) => <p className="mb-2 last:mb-0 text-sm leading-relaxed" {...props} />,
                    strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
                    ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 text-sm" {...props} />,
                    ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 text-sm" {...props} />,
                    li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                  }}
                >
                  {qaAnswer.answer}
                </ReactMarkdown>
              </div>
              {qaAnswer.sources && qaAnswer.sources.length > 0 && (
                <div className="px-4 py-3 border-t bg-gray-50 rounded-b-lg">
                  <p className="text-xs font-medium text-gray-500 mb-2">Sources ({qaAnswer.sources.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {qaAnswer.sources.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-primaryDark rounded text-xs">
                        <FileText className="w-3 h-3" />
                        {s.filename}
                        <span className="text-primary ml-1">{Math.round(s.relevance * 100)}%</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Q&A History */}
      {qaHistory.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 rounded-t-lg">
            <h3 className="font-semibold text-gray-900">Recent Questions</h3>
          </div>
          <div className="divide-y max-h-80 overflow-y-auto">
            {qaHistory.map((q) => (
              <div key={q.id} className="px-4 py-3 space-y-1">
                <p className="text-sm font-medium text-gray-800">{q.question}</p>
                <p className="text-xs text-gray-500 line-clamp-2">{q.answer}</p>
                <p className="text-xs text-gray-400">
                  {q.asked_at ? new Date(q.asked_at).toLocaleString() : ''}
                  {q.sources && q.sources.length > 0 && ` · ${q.sources.length} sources`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Documents tab ─────────────────────────────────────────────────────────────

function DocumentsTab() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchMode, setSearchMode] = useState('filename');
  const [selectedDoc, setSelectedDoc] = useState(null);

  useEffect(() => {
    const delay = searchMode === 'semantic' ? 500 : 200;
    const timer = setTimeout(() => setDebouncedSearch(search), delay);
    return () => clearTimeout(timer);
  }, [search, searchMode]);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      if (debouncedSearch && searchMode === 'semantic') {
        const result = await ingestionService.semanticSearch(debouncedSearch, 20);
        setDocuments((result?.results || []).map((r) => ({
          id: r.id,
          filename: r.filename,
          filepath: r.filepath,
          file_type: r.doc_type,
          doc_type: r.doc_type,
          relevance: r.relevance,
          excerpt: r.excerpt || '',
        })));
      } else {
        const data = await ingestionService.listDocuments(debouncedSearch ? { search: debouncedSearch } : undefined);
        setDocuments(Array.isArray(data) ? data : []);
      }
    } catch { setDocuments([]); }
    finally { setLoading(false); }
  }, [debouncedSearch, searchMode]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={searchMode === 'semantic' ? 'Search by meaning...' : 'Search by filename...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex rounded-md border overflow-hidden">
          {['filename', 'semantic'].map((m) => (
            <button
              key={m}
              onClick={() => setSearchMode(m)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors capitalize ${
                searchMode === m ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
        <button onClick={loadDocuments} className="p-2 border border-gray-300 rounded-md hover:bg-gray-50">
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {selectedDoc ? (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primaryDark" />
              {selectedDoc.filename}
            </h3>
            <button onClick={() => setSelectedDoc(null)} className="px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50">Back</button>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex gap-3 text-sm text-gray-600">
              <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{selectedDoc.file_type || selectedDoc.doc_type || '-'}</span>
              <span>{selectedDoc.char_count?.toLocaleString() ?? '0'} chars</span>
              {selectedDoc.page_count && <span>{selectedDoc.page_count} pages</span>}
            </div>
            <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">{selectedDoc.extracted_text}</pre>
            </div>
          </div>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : documents.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500 text-sm">
          No documents found. Scan a folder first.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-blue-600 text-white">
                <th className="px-4 py-3 text-left text-xs font-bold uppercase">Filename</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase">Type</th>
                {searchMode === 'semantic' && search ? (
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Relevance</th>
                ) : (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Characters</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Pages</th>
                  </>
                )}
                <th className="px-4 py-3 text-left text-xs font-bold uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {documents.map((doc) => (
                <tr
                  key={doc.id}
                  className="hover:bg-blue-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedDoc(doc)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-primaryDark">{doc.filename}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{doc.file_type || doc.doc_type || '-'}</span>
                  </td>
                  {searchMode === 'semantic' && search ? (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div className="h-2 rounded-full bg-blue-500" style={{ width: `${(doc.relevance ?? 0) * 100}%` }} />
                        </div>
                        <span className="text-gray-700 font-medium text-xs">{((doc.relevance ?? 0) * 100).toFixed(1)}%</span>
                      </div>
                    </td>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-600">{doc.char_count?.toLocaleString() ?? '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{doc.page_count ?? '-'}</td>
                    </>
                  )}
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main DocumentBrowser component (tabs: Documents, Taxonomy, Q&A) ──────────

const DocumentBrowser = () => {
  const [activeTab, setActiveTab] = useState('documents');

  const tabs = [
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'taxonomy',  label: 'Taxonomy',  icon: Tags },
    { id: 'qa',        label: 'Q&A',       icon: null },
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto scrollbar-none whitespace-nowrap">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px shrink-0 ${
              activeTab === id
                ? 'border-blue-600 text-primaryDark'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab(id)}
          >
            {Icon && <Icon className="w-4 h-4" />}
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'documents' && <DocumentsTab />}
      {activeTab === 'taxonomy'  && <TaxonomyTab />}
      {activeTab === 'qa'        && <QASection />}
    </div>
  );
};

export default DocumentBrowser;
